import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, RotateCcw, Loader2, ImageIcon, Film, Check, Maximize, Minimize } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";
import { useAssets } from "@/context/AssetsContext";
import ImageEditor from "@/components/ImageEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const SLOT_DEFAULTS = {
  logo: { aspect: "original", fit: "contain" },
  share_image: { aspect: "share", fit: "cover" },
  video_thumbnail: { aspect: "landscape", fit: "cover" },
};
const defaultsFor = (slot) => SLOT_DEFAULTS[slot] || { aspect: "phone", fit: "cover" };

const GROUPS = [
  {
    title: "Brand",
    hint: "Your logo appears in the header, footer and admin. The share image is used when links are posted on social media.",
    slots: [
      { slot: "logo", label: "Logo", accept: "image/*" },
      { slot: "share_image", label: "Social Share Image", accept: "image/*" },
    ],
  },
  {
    title: "App Screenshots",
    hint: "Uploaded screenshots replace the placeholder phone screens across the site (hero, How It Works, features, live tracking).",
    slots: [
      { slot: "screen_home", label: "Home Screen", accept: "image/*" },
      { slot: "screen_chat", label: "Describe Problem", accept: "image/*" },
      { slot: "screen_ai", label: "AI Analyzing", accept: "image/*" },
      { slot: "screen_providers", label: "Nearby Providers", accept: "image/*" },
      { slot: "screen_tracking", label: "Live Tracking", accept: "image/*" },
      { slot: "screen_done", label: "Completed", accept: "image/*" },
    ],
  },
];

function Slot({ entry, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [editFile, setEditFile] = useState(null);
  const url = entry.url ? `${BACKEND_URL}${entry.url}` : null;
  const def = defaultsFor(entry.slot);

  const pickFile = (file) => {
    if (!file) return;
    setEditFile(file);
  };

  const upload = async ({ file, fit, bg }) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("fit", fit);
    fd.append("bg", bg);
    try {
      await api.post(`/admin/assets/${entry.slot}`, fd);
      toast.success(`${entry.label} updated`);
      setEditFile(null);
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const setFit = async (fit) => {
    setBusy(true);
    try { await api.patch(`/admin/assets/${entry.slot}/display`, { fit, bg: entry.bg || "#0A0A0A" }); await onChanged(); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try { await api.delete(`/admin/assets/${entry.slot}`); toast.message(`${entry.label} reset to default`); await onChanged(); }
    catch { toast.error("Failed to reset"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-black/5 bg-white p-4" data-testid={`asset-slot-${entry.slot}`}>
      <div className="mb-3 flex aspect-square items-center justify-center overflow-hidden rounded-xl ring-1 ring-black/5"
        style={{ background: entry.fit === "contain" ? (entry.bg || "#0A0A0A") : "#FAFAFC" }}>
        {url ? <img src={url} alt={entry.label} className={`h-full w-full ${entry.fit === "contain" ? "object-contain" : "object-cover"}`} />
          : <ImageIcon className="h-8 w-8 text-neutral-300" />}
      </div>
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-neutral-800">{entry.label}</p>
        {entry.set && <span className="flex items-center gap-1 text-[11px] font-semibold text-ai"><Check className="h-3 w-3" /> Set</span>}
      </div>

      {entry.set && (
        <div className="mt-2 grid grid-cols-2 gap-1.5">
          <button onClick={() => setFit("cover")} disabled={busy}
            className={`flex items-center justify-center gap-1 rounded-md border py-1 text-[11px] font-semibold ${entry.fit !== "contain" ? "border-brand bg-brand/10 text-brand" : "border-black/10 text-neutral-500"}`} data-testid={`asset-fit-cover-${entry.slot}`}><Maximize className="h-3 w-3" /> Fill</button>
          <button onClick={() => setFit("contain")} disabled={busy}
            className={`flex items-center justify-center gap-1 rounded-md border py-1 text-[11px] font-semibold ${entry.fit === "contain" ? "border-brand bg-brand/10 text-brand" : "border-black/10 text-neutral-500"}`} data-testid={`asset-fit-contain-${entry.slot}`}><Minimize className="h-3 w-3" /> Fit</button>
        </div>
      )}

      <input ref={inputRef} type="file" accept={entry.accept || "image/*"} className="hidden"
        onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ""; }} data-testid={`asset-input-${entry.slot}`} />
      <div className="mt-3 flex gap-2">
        <Button size="sm" onClick={() => inputRef.current?.click()} disabled={busy}
          className="h-9 flex-1 gap-1.5 rounded-lg bg-brand text-xs hover:bg-brand-dark" data-testid={`asset-upload-${entry.slot}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />}
          {entry.set ? "Replace" : "Upload"}
        </Button>
        {entry.set && (
          <Button size="sm" variant="outline" onClick={reset} disabled={busy} className="h-9 rounded-lg" data-testid={`asset-reset-${entry.slot}`}>
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      <ImageEditor open={Boolean(editFile)} file={editFile} slot={entry.slot}
        defaultAspect={def.aspect} defaultFit={entry.fit || def.fit}
        onConfirm={upload} onCancel={() => setEditFile(null)} />
    </div>
  );
}

function VideoSlot({ entry, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [urlInput, setUrlInput] = useState(entry.video_url || "");
  const fileUrl = entry.url ? `${BACKEND_URL}${entry.url}` : null;

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post("/admin/assets/promo_video", fd); toast.success("Video uploaded"); await onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const saveUrl = async () => {
    setBusy(true);
    try { await api.post("/admin/assets/promo_video/url", { url: urlInput }); toast.success("Video link saved"); await onChanged(); }
    catch { toast.error("Failed to save link"); }
    finally { setBusy(false); }
  };
  const reset = async () => {
    setBusy(true);
    try { await api.delete("/admin/assets/promo_video"); setUrlInput(""); toast.message("Video reset"); await onChanged(); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-black/5 bg-white p-5" data-testid="asset-slot-promo_video">
      <div className="mb-4 flex aspect-video items-center justify-center overflow-hidden rounded-xl bg-neutral-900 ring-1 ring-black/5">
        {fileUrl ? <video src={fileUrl} controls muted className="h-full w-full object-cover" />
          : entry.video_url ? <div className="text-center text-sm text-white/70"><Film className="mx-auto mb-2 h-8 w-8" />Embedded link set</div>
          : <Film className="h-10 w-10 text-white/20" />}
      </div>
      <input ref={inputRef} type="file" accept="video/mp4,video/webm" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="asset-input-promo_video" />
      <div className="grid gap-3 sm:grid-cols-2">
        <Button onClick={() => inputRef.current?.click()} disabled={busy} className="h-10 gap-2 rounded-lg bg-brand hover:bg-brand-dark" data-testid="asset-upload-promo_video">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Upload MP4
        </Button>
        {(entry.url || entry.video_url) && (
          <Button variant="outline" onClick={reset} disabled={busy} className="h-10 gap-2 rounded-lg" data-testid="asset-reset-promo_video"><RotateCcw className="h-4 w-4" /> Reset</Button>
        )}
      </div>
      <div className="mt-3 flex gap-2">
        <Input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="Or paste a YouTube / Vimeo URL" className="h-10 rounded-lg" data-testid="asset-video-url" />
        <Button variant="secondary" onClick={saveUrl} disabled={busy} className="h-10 rounded-lg" data-testid="asset-video-url-save">Save link</Button>
      </div>
      <p className="mt-2 text-xs text-neutral-400">An uploaded MP4 takes priority over a link. Video is always muted on autoplay.</p>
    </div>
  );
}

export default function AssetManager() {
  const { refresh: refreshPublic } = useAssets();
  const [list, setList] = useState([]);

  const load = async () => {
    const r = await api.get("/admin/assets");
    setList(r.data);
    refreshPublic();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const bySlot = Object.fromEntries(list.map((e) => [e.slot, e]));
  const video = bySlot["promo_video"] || { slot: "promo_video" };

  return (
    <div className="space-y-10" data-testid="asset-manager">
      <div className="rounded-2xl border border-brand/15 bg-brand/5 p-5 text-sm text-neutral-600">
        Upload your real brand assets here — no code required. Changes go live on the site immediately.
        Recommended: logo as a transparent PNG, screenshots at a 9:19.5 phone ratio, share image at 1200×630.
      </div>

      {GROUPS.map((g) => (
        <div key={g.title}>
          <h3 className="text-lg font-bold tracking-tight text-neutral-900">{g.title}</h3>
          <p className="mb-4 mt-1 max-w-2xl text-sm text-neutral-500">{g.hint}</p>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {g.slots.map((s) => (
              <Slot key={s.slot} entry={{ ...s, ...(bySlot[s.slot] || {}) }} onChanged={load} />
            ))}
          </div>
        </div>
      ))}

      <div>
        <h3 className="text-lg font-bold tracking-tight text-neutral-900">Promotional Video</h3>
        <p className="mb-4 mt-1 max-w-2xl text-sm text-neutral-500">Set the preview image people see before pressing play, then upload an MP4 or embed a YouTube / Vimeo link. It appears in the “See SOSConnect AI in action” section.</p>
        <div className="grid items-start gap-4 lg:grid-cols-[260px_1fr]">
          <Slot entry={{ slot: "video_thumbnail", label: "Video Preview Image", accept: "image/*", ...(bySlot["video_thumbnail"] || {}) }} onChanged={load} />
          <VideoSlot entry={video} onChanged={load} />
        </div>
      </div>
    </div>
  );
}
